import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { 
  Home, Trophy, History, User, Settings, Wallet, LogOut, Bell,
  CheckCircle, Zap, Gift, Filter, Download, Search,
  ArrowUpCircle, ArrowDownCircle
} from "lucide-react";

// Random tabs for browser frame
const RANDOM_TABS = [
  { name: 'YouTube', favicon: 'https://www.youtube.com/favicon.ico' },
  { name: 'ВКонтакте', favicon: 'https://vk.com/favicon.ico' },
  { name: 'Яндекс', favicon: 'https://yandex.ru/favicon.ico' },
  { name: 'Google', favicon: 'https://www.google.com/favicon.ico' },
  { name: 'Telegram', favicon: 'https://telegram.org/favicon.ico' },
  { name: 'WhatsApp', favicon: 'https://web.whatsapp.com/favicon.ico' },
  { name: 'Mail.ru', favicon: 'https://mail.ru/favicon.ico' },
  { name: 'Авито', favicon: 'https://www.avito.ru/favicon.ico' },
];

// Browser Frame Component with theme support and random tabs
const BrowserFrame = ({ theme = 'light', urlPath = '/history' }) => {
  const isLight = theme === 'light';
  const bgColor = isLight ? '#dee1e6' : '#202124';
  const tabBgInactive = isLight ? '#c8ccd1' : '#35363a';
  const tabBgActive = isLight ? '#fff' : '#1a1f2e';
  const textColor = isLight ? '#5f6368' : '#9aa0a6';
  const textColorActive = isLight ? '#202124' : '#e8eaed';
  const addressBarBg = isLight ? '#f1f3f4' : '#35363a';
  const toolbarBg = isLight ? '#f1f3f4' : '#292a2d';
  
  // Generate random additional tabs (0-2 extra tabs after Liga Stavok)
  const randomTabCount = Math.floor(Math.random() * 3); // 0, 1, or 2 additional tabs
  const shuffledTabs = [...RANDOM_TABS].sort(() => Math.random() - 0.5);
  const additionalTabs = shuffledTabs.slice(0, randomTabCount);
  
  return (
    <div className="flex-shrink-0" style={{ backgroundColor: bgColor }}>
      <div className="flex items-center h-[38px]" style={{ backgroundColor: bgColor }}>
        <div className="flex items-end flex-1 pl-2">
          {/* Tab 1 - Ozon Банк (always first, inactive) */}
          <div className="flex items-center h-[34px] px-3 rounded-t-lg min-w-[120px] max-w-[180px] mr-[1px] cursor-pointer" style={{ backgroundColor: tabBgInactive }}>
            <img src="/ozon-favicon.ico" alt="" className="w-4 h-4 mr-2 flex-shrink-0" onError={(e) => e.target.style.display='none'} />
            <span className="text-[13px] truncate flex-1" style={{ color: textColor }}>Ozon Банк</span>
          </div>
          
          {/* Tab 2 - Лига Ставок (ACTIVE) */}
          <div className="flex items-center h-[34px] px-3 rounded-t-lg min-w-[120px] max-w-[180px] mr-[1px] cursor-pointer" style={{ backgroundColor: tabBgActive }}>
            <img src="https://ligastavok.ru/favicon.ico" alt="" className="w-4 h-4 mr-2 flex-shrink-0" onError={(e) => e.target.style.display='none'} />
            <span className="text-[13px] truncate flex-1" style={{ color: textColorActive }}>Лига Ставок</span>
          </div>
          
          {/* Random additional tabs (0-2) */}
          {additionalTabs.map((tab, index) => (
            <div key={index} className="flex items-center h-[34px] px-3 rounded-t-lg min-w-[100px] max-w-[160px] mr-[1px] cursor-pointer" style={{ backgroundColor: tabBgInactive }}>
              <img src={tab.favicon} alt="" className="w-4 h-4 mr-2 flex-shrink-0" onError={(e) => e.target.style.display='none'} />
              <span className="text-[13px] truncate flex-1" style={{ color: textColor }}>{tab.name}</span>
            </div>
          ))}
          
          <button className="w-7 h-7 flex items-center justify-center rounded-full ml-1 mb-1" style={{ color: textColor }}>
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/></svg>
          </button>
        </div>
        <div className="flex items-center h-full">
          <button className="w-[46px] h-full flex items-center justify-center hover:bg-[#3c3d41]" style={{ color: textColor }}><svg className="w-[10px] h-[1px]" viewBox="0 0 10 1"><rect fill="currentColor" width="10" height="1"/></svg></button>
          <button className="w-[46px] h-full flex items-center justify-center hover:bg-[#3c3d41]" style={{ color: textColor }}><svg className="w-[10px] h-[10px]" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1"><rect x="0.5" y="0.5" width="9" height="9"/></svg></button>
          <button className="w-[46px] h-full flex items-center justify-center hover:bg-[#e81123] hover:text-white" style={{ color: textColor }}><svg className="w-[10px] h-[10px]" viewBox="0 0 10 10" stroke="currentColor" strokeWidth="1.2"><line x1="0" y1="0" x2="10" y2="10"/><line x1="10" y1="0" x2="0" y2="10"/></svg></button>
        </div>
      </div>
      <div className="flex items-center h-[40px] px-2" style={{ backgroundColor: toolbarBg }}>
        <div className="flex items-center gap-1 mr-2">
          <button className="w-8 h-8 flex items-center justify-center rounded-full" style={{ color: textColor }}><svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/></svg></button>
          <button className="w-8 h-8 flex items-center justify-center rounded-full" style={{ color: isLight ? '#dadce0' : '#5f6368' }}><svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/></svg></button>
          <button className="w-8 h-8 flex items-center justify-center rounded-full" style={{ color: textColor }}><svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/></svg></button>
        </div>
        <div className="flex-1 flex items-center h-[32px] px-4 rounded-full" style={{ backgroundColor: addressBarBg }}>
          <svg className="w-4 h-4 mr-3 flex-shrink-0" style={{ color: textColor }} viewBox="0 0 24 24" fill="currentColor"><path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/></svg>
          <span className="text-[14px]" style={{ color: textColorActive }}>ligastavok.ru{urlPath}</span>
        </div>
        <div className="flex items-center gap-1 ml-3">
          <button className="w-8 h-8 flex items-center justify-center rounded-full" style={{ color: textColor }}><svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg></button>
          <button className="w-8 h-8 flex items-center justify-center rounded-full" style={{ color: textColor }}><svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="2"/><circle cx="12" cy="5" r="2"/><circle cx="12" cy="19" r="2"/></svg></button>
        </div>
      </div>
    </div>
  );
};

export default function LigaStavokHistory() {
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('all');
  const [showBrowser, setShowBrowser] = useState(true);
  const [browserTheme, setBrowserTheme] = useState('light');

  useEffect(() => {
    const dataParam = searchParams.get('data');
    const browserParam = searchParams.get('showBrowser');
    const themeParam = searchParams.get('theme');
    
    let parsed = null;
    
    if (dataParam) {
      try {
        parsed = (() => { try { return JSON.parse(dataParam); } catch(_) { return JSON.parse(decodeURIComponent(dataParam)); } })();
      } catch (e) {}
    }
    
    if (!parsed) {
      try {
        const stored = localStorage.getItem('ligastavok_preview_data');
        if (stored) parsed = JSON.parse(stored);
      } catch (e) {}
    }
    
    if (parsed) {
      setData(parsed);
      // Only set browser settings from data if URL params are NOT present
      if (browserParam === null && parsed.showBrowser !== undefined) {
        setShowBrowser(parsed.showBrowser);
      }
      if (!themeParam && parsed.browserTheme) {
        setBrowserTheme(parsed.browserTheme);
      }
    }
    
    // URL params always take priority
    if (browserParam !== null) {
      setShowBrowser(browserParam !== 'false');
    }
    if (themeParam) {
      setBrowserTheme(themeParam);
    }
  }, [searchParams]);

  if (!data) {
    return (
      <div className="min-h-screen bg-[#1a1f2e] flex items-center justify-center">
        <div className="text-white text-lg">Загрузка...</div>
      </div>
    );
  }

  const menuItems = [
    { icon: Home, label: 'Главная' },
    { icon: Trophy, label: 'Спорт' },
    { icon: Zap, label: 'Live' },
    { icon: Gift, label: 'Казино' },
    { icon: History, label: 'История ставок', active: true },
    { icon: Wallet, label: 'Кошелёк' },
    { icon: User, label: 'Профиль' },
    { icon: Settings, label: 'Настройки' },
  ];

  const filteredBets = (data.bets || []).filter(bet => {
    if (filter === 'win') return bet.result === 'win';
    if (filter === 'lose') return bet.result === 'lose';
    return true;
  });

  const totalStake = filteredBets.reduce((sum, b) => sum + parseFloat(b.amount || 0), 0);
  const totalPayout = filteredBets.filter(b => b.result === 'win').reduce((sum, b) => sum + parseFloat(b.payout || 0), 0);

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#1a1f2e', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      {showBrowser && <BrowserFrame theme={browserTheme} urlPath="/history" />}
      <div className="flex-1 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-[#0f1520] flex flex-col border-r border-[#2a3441]">
        <div className="p-4 border-b border-[#2a3441]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#1a5fb4] rounded-lg flex items-center justify-center">
              <span className="text-white font-black text-lg">1X</span>
            </div>
            <div>
              <h1 className="text-white font-bold text-lg">ЛИГА СТАВОК</h1>
              <p className="text-[#8b9caf] text-xs">Betting & Casino</p>
            </div>
          </div>
        </div>

        <div className="p-4 border-b border-[#2a3441]">
          <div className="bg-gradient-to-r from-[#1a5fb4] to-[#2563eb] rounded-xl p-4">
            <p className="text-blue-200 text-xs mb-1">Баланс</p>
            <p className="text-white text-2xl font-bold">{data.balance?.toLocaleString()} ₽</p>
            <div className="flex gap-2 mt-3">
              <button className="flex-1 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-white text-xs font-medium flex items-center justify-center gap-1">
                <ArrowUpCircle className="w-3 h-3" /> Пополнить
              </button>
              <button className="flex-1 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white text-xs font-medium flex items-center justify-center gap-1">
                <ArrowDownCircle className="w-3 h-3" /> Вывести
              </button>
            </div>
          </div>
        </div>

        <nav className="flex-1 py-2">
          {menuItems.map((item, index) => {
            const Icon = item.icon;
            return (
              <button
                key={index}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-all ${
                  item.active 
                    ? 'bg-[#1a5fb4] text-white' 
                    : 'text-[#8b9caf] hover:bg-[#1a2332] hover:text-white'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>

        <div className="p-3 border-t border-[#2a3441]">
          <button className="w-full flex items-center gap-3 px-4 py-2 text-[#8b9caf] hover:text-white hover:bg-[#1a2332] rounded-lg text-sm">
            <LogOut className="w-4 h-4" />
            <span>Выйти</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        <header className="bg-[#0f1520] border-b border-[#2a3441] px-6 py-3 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white">История ставок</h2>
            <p className="text-[#8b9caf] text-sm">Все ваши ставки</p>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-green-500/10 border border-green-500/30 rounded-full">
              <CheckCircle className="w-4 h-4 text-green-400" />
              <span className="text-green-400 text-sm font-medium">Верифицирован</span>
            </div>
            
            <div className="flex items-center gap-3 pl-4 border-l border-[#2a3441]">
              <div className="w-9 h-9 bg-gradient-to-br from-[#1a5fb4] to-[#2563eb] rounded-full flex items-center justify-center">
                <span className="text-white text-sm font-bold">
                  {data.firstName?.[0]}{data.lastName?.[0]}
                </span>
              </div>
              <div>
                <p className="text-sm font-medium text-white">{data.fullName}</p>
                <p className="text-xs text-[#8b9caf]">ID: {data.userId}</p>
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 p-6 overflow-auto">
          {/* Stats */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="bg-[#0f1520] rounded-xl p-4 border border-[#2a3441]">
              <p className="text-[#8b9caf] text-sm">Всего ставок</p>
              <p className="text-white text-2xl font-bold">{data.totalBets}</p>
            </div>
            <div className="bg-[#0f1520] rounded-xl p-4 border border-[#2a3441]">
              <p className="text-[#8b9caf] text-sm">Выигрышей</p>
              <p className="text-green-400 text-2xl font-bold">{data.totalWins}</p>
            </div>
            <div className="bg-[#0f1520] rounded-xl p-4 border border-[#2a3441]">
              <p className="text-[#8b9caf] text-sm">Поставлено</p>
              <p className="text-white text-2xl font-bold">{totalStake.toLocaleString()} ₽</p>
            </div>
            <div className="bg-[#0f1520] rounded-xl p-4 border border-[#2a3441]">
              <p className="text-[#8b9caf] text-sm">Выиграно</p>
              <p className="text-green-400 text-2xl font-bold">+{totalPayout.toLocaleString()} ₽</p>
            </div>
          </div>

          {/* Bets Table */}
          <div className="bg-[#0f1520] rounded-2xl border border-[#2a3441] overflow-hidden">
            <div className="p-4 border-b border-[#2a3441] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-[#8b9caf]" />
                <div className="flex gap-1 bg-[#1a2332] p-1 rounded-lg">
                  {[
                    { id: 'all', label: 'Все' },
                    { id: 'win', label: 'Выигрыш' },
                    { id: 'lose', label: 'Проигрыш' },
                  ].map(f => (
                    <button
                      key={f.id}
                      onClick={() => setFilter(f.id)}
                      className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                        filter === f.id 
                          ? 'bg-[#1a5fb4] text-white' 
                          : 'text-[#8b9caf] hover:text-white'
                      }`}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>
              
              <button className="flex items-center gap-2 px-4 py-2 text-[#8b9caf] hover:text-white border border-[#2a3441] rounded-lg text-sm">
                <Download className="w-4 h-4" />
                Экспорт
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#1a2332]">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-[#8b9caf] uppercase">Событие</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-[#8b9caf] uppercase">Тип</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-[#8b9caf] uppercase">Коэф.</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-[#8b9caf] uppercase">Ставка</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-[#8b9caf] uppercase">Результат</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-[#8b9caf] uppercase">Дата</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#2a3441]">
                  {filteredBets.map((bet, index) => (
                    <tr key={index} className="hover:bg-[#1a2332] transition-colors">
                      <td className="px-6 py-4">
                        <div>
                          <p className="text-white font-medium">{bet.event}</p>
                          <p className="text-[#8b9caf] text-sm">{bet.sport}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-2 py-1 bg-[#1a5fb4]/20 text-[#60a5fa] text-sm rounded">{bet.betType}</span>
                      </td>
                      <td className="px-6 py-4 text-white font-medium">{bet.odds}</td>
                      <td className="px-6 py-4 text-white">{parseInt(bet.amount).toLocaleString()} ₽</td>
                      <td className="px-6 py-4">
                        {bet.result === 'win' ? (
                          <div>
                            <span className="px-2 py-1 bg-green-500/10 text-green-400 text-sm rounded">Выигрыш</span>
                            <p className="text-green-400 text-sm mt-1">+{parseInt(bet.payout).toLocaleString()} ₽</p>
                          </div>
                        ) : (
                          <span className="px-2 py-1 bg-red-500/10 text-red-400 text-sm rounded">Проигрыш</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-[#8b9caf] text-sm">{bet.date}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </main>
      </div>
      </div>
    </div>
  );
}
